import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Users, Package, Gamepad2, ChevronRight, Eye, Zap, Lock } from 'lucide-react';
import API from '@/utils/api';
import ListingCard from '@/components/ListingCard';

const LOGO = process.env.REACT_APP_LOGO_URL;

export default function Home() {
  const [games, setGames] = useState([]);
  const [listings, setListings] = useState([]);

  useEffect(() => {
    API.get('/games').then(r => setGames(r.data.slice(0, 8)));
    API.get('/listings?limit=8&sort_by=newest').then(r => setListings(r.data.listings || []));
  }, []);

  return (
    <div className="text-white overflow-x-hidden">
      {/* HERO */}
      <section className="relative min-h-[88vh] flex items-center justify-center overflow-hidden">
        {/* Background layers */}
        <div className="absolute inset-0 bg-[#080808]" />
        {/* Radial red ambient */}
        <div className="absolute inset-0"
          style={{ background: 'radial-gradient(ellipse 80% 60% at 50% 100%, rgba(229,9,20,0.07) 0%, transparent 70%)' }} />
        {/* Subtle grid */}
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '80px 80px' }} />
        {/* Left red accent line */}
        <div className="absolute left-0 top-0 bottom-0 w-[1px] bg-gradient-to-b from-transparent via-rm-red/30 to-transparent hidden lg:block" />

        <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
          {/* Logo */}
          <div className="flex justify-center mb-8 animate-fade-up">
            <div className="relative">
              <img src={LOGO} alt="RobloxMart" className="h-24 md:h-32 w-auto drop-shadow-2xl" data-testid="hero-logo" />
              <div className="absolute inset-0 bg-rm-red/20 blur-2xl rounded-full scale-150 opacity-50" />
            </div>
          </div>

          {/* Headline */}
          <div className="animate-fade-up delay-100">
            <p className="text-[10px] md:text-xs font-rajdhani font-bold uppercase tracking-[0.4em] text-rm-red mb-4">
              The Premier Roblox Marketplace
            </p>
            <h1 className="font-rajdhani font-bold text-6xl md:text-8xl uppercase leading-none tracking-tight text-white mb-6">
              ROBLOX<span className="text-rm-red" style={{ textShadow: '0 0 40px rgba(229,9,20,0.4)' }}>MART</span>
            </h1>
            <p className="text-zinc-400 text-base md:text-lg mb-10 max-w-lg mx-auto leading-relaxed font-light">
              Connect with trusted traders for Roblox items. Fast, safe, community-driven.
            </p>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-up delay-200">
            <Link to="/browse" data-testid="hero-browse-btn"
              className="btn-primary btn-cta px-10 py-4 rounded-sm flex items-center justify-center gap-2.5 text-sm">
              Browse Listings <ArrowRight size={16} />
            </Link>
            <Link to="/auth?tab=register" data-testid="hero-register-btn"
              className="btn-outline px-10 py-4 rounded-sm flex items-center justify-center gap-2.5 text-sm">
              Become a Seller
            </Link>
          </div>

          {/* Stats row */}
          <div className="mt-16 flex flex-wrap justify-center gap-2 md:gap-0 animate-fade-up delay-300">
            {[
              { icon: Users, label: 'Active Sellers', val: '100+' },
              { icon: Package, label: 'Listings', val: '500+' },
              { icon: Gamepad2, label: 'Roblox Games', val: `${games.length || '4'}+` },
            ].map(({ icon: Icon, label, val }, i) => (
              <div key={i} className={`flex flex-col items-center px-8 md:px-12 ${i !== 2 ? 'md:border-r border-white/5' : ''}`}>
                <p className="font-rajdhani font-bold text-3xl text-white mb-1" style={{ textShadow: '0 0 20px rgba(229,9,20,0.2)' }}>
                  {val}
                </p>
                <p className="text-[10px] text-zinc-600 uppercase tracking-[0.15em]">{label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom fade */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
      </section>

      {/* DISCLAIMER BANNER */}
      <div className="bg-[#0f0f0f] border-y border-[#1a1a1a]" data-testid="disclaimer-banner">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-center gap-2.5 text-center">
          <ShieldCheck size={14} className="text-rm-red shrink-0" />
          <p className="text-[11px] text-zinc-500">
            <span className="text-zinc-300 font-medium">RobloxMart</span> is not affiliated with Roblox Corporation and does not process payments. We only connect buyers and sellers.
          </p>
        </div>
      </div>

      {/* GAMES */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-2">Game Library</p>
            <h2 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight" data-testid="games-section-title">
              Browse by <span className="text-white">Game</span>
            </h2>
          </div>
          <Link to="/browse" className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-600 hover:text-rm-red transition-colors font-rajdhani font-semibold uppercase tracking-widest">
            View All <ChevronRight size={12} />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {games.map((game, i) => (
            <Link key={game.id} to={`/browse?game=${game.id}`} data-testid={`game-card-${game.id}`}
              className={`relative rounded-sm overflow-hidden border border-[#1e1e1e] card-hover group cursor-pointer animate-fade-in delay-${Math.min((i % 4 + 1) * 100, 400)}`}>
              <div className="aspect-video bg-[#111]">
                {game.banner_url ? (
                  <img src={game.banner_url} alt={game.name}
                    className="w-full h-full object-cover group-hover:scale-[1.08] transition-transform duration-700 ease-out brightness-75 group-hover:brightness-90" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Gamepad2 size={28} className="text-zinc-700" />
                  </div>
                )}
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <h3 className="font-rajdhani font-bold text-white uppercase tracking-wide text-sm leading-tight">{game.name}</h3>
              </div>
              {/* Red accent on hover */}
              <div className="absolute inset-x-0 bottom-0 h-[2px] bg-rm-red scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
            </Link>
          ))}
        </div>
      </section>

      {/* RECENT LISTINGS */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 pb-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-2">Latest Drops</p>
            <h2 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight">
              Recent <span className="text-white">Listings</span>
            </h2>
          </div>
          <Link to="/browse" className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-600 hover:text-rm-red transition-colors font-rajdhani font-semibold uppercase tracking-widest">
            View All <ChevronRight size={12} />
          </Link>
        </div>

        {listings.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {listings.map((l, i) => (
              <div key={l.id} className={`animate-fade-in delay-${Math.min(i * 50, 400)}`}>
                <ListingCard listing={l} />
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-[#1a1a1a] rounded-sm bg-[#0d0d0d]">
            <Package size={36} className="mx-auto mb-4 text-zinc-800" />
            <p className="text-zinc-500 mb-2 font-rajdhani font-bold uppercase tracking-wider">No listings yet</p>
            <p className="text-xs text-zinc-700 mb-6">Be the first to list a Roblox item</p>
            <Link to="/auth?tab=register" className="btn-primary px-6 py-3 rounded-sm text-sm inline-flex">
              Start Selling
            </Link>
          </div>
        )}
      </section>

      {/* HOW IT WORKS */}
      <section className="relative border-t border-[#151515] py-20 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-14">
            <p className="text-[10px] font-rajdhani font-bold uppercase tracking-[0.3em] text-rm-red mb-2">Simple Process</p>
            <h2 className="font-rajdhani font-bold text-4xl md:text-5xl uppercase tracking-tight">
              How It <span className="text-rm-red">Works</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[#151515]">
            {[
              { step: '01', icon: Eye, title: 'Browse Listings', desc: 'Explore thousands of Roblox items across all popular games. Filter by price, game, and item type.' },
              { step: '02', icon: Zap, title: 'Reveal Contact', desc: 'Click "Show Seller Contact" to instantly get the seller\'s contact details. No escrow, no fees.' },
              { step: '03', icon: Lock, title: 'Trade Safely', desc: 'Complete your trade directly with the seller. Use our Discord for trusted middleman services.' },
            ].map(({ step, icon: Icon, title, desc }) => (
              <div key={step} className="group bg-[#0a0a0a] p-8 md:p-10 hover:bg-[#0d0d0d] transition-colors duration-300 relative overflow-hidden">
                <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-rm-red/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <p className="font-rajdhani font-bold text-6xl text-[#1a1a1a] mb-6 leading-none">{step}</p>
                <div className="w-10 h-10 bg-rm-red/10 border border-rm-red/20 rounded-sm flex items-center justify-center mb-4">
                  <Icon size={18} className="text-rm-red" />
                </div>
                <h3 className="font-rajdhani font-bold text-lg uppercase tracking-wide mb-3">{title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
